import { Button, formatDate, ListView, PageHeader, useCursorList } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Checkbox } from "@majitap/ui/arc/checkbox/checkbox";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { useFormCheck } from "../../form-check";
import { filterSearch } from "../../list-search";
import { type Role, rolesQuery, staffBadge, teamActions } from "../../team";
import { RolesView } from "./roles";
import styles from "./settings.module.css";

const STATES = ["active", "invited", "deactivated"] as const;
export const staffFilters = filterSearch({ view: ["people", "roles"] as const, state: [...STATES, "all"] as const });

/** Staff & roles (P8-22, `ThaQb` in the design): who works in the console, and what each role may do. */
export function StaffPage(): ReactNode {
  const search = useSearch({ from: "/console/settings/staff" });
  const navigate = useNavigate({ from: "/settings/staff" });
  const format = useCompanyFormat();
  const view = search.view ?? "people";
  const state = search.state ?? "active";
  const [inviting, setInviting] = useState(false);
  const list = useCursorList({
    queryKey: ["staff", "list", state],
    fetchPage: (cursor) =>
      ok(
        api.GET("/v1/admin/staff", {
          params: { query: { state: state === "all" ? undefined : state, cursor, limit: 25 } },
        }),
      ),
  });
  return (
    <>
      <PageHeader
        title="Staff & roles"
        description="Who works in the console, and what each role may do."
        actions={view === "people" ? <Button onClick={() => setInviting(true)}>Invite staff</Button> : undefined}
      />
      <SegmentedControl
        label="Show"
        value={view}
        onValueChange={(v) => navigate({ search: { view: v === "people" ? undefined : "roles" } })}
        options={[
          { value: "people", label: "Staff" },
          { value: "roles", label: "Roles" },
        ]}
      />
      {view === "roles" ? (
        <RolesView />
      ) : (
        <ListView
          caption="Staff"
          itemName={{ one: "person", other: "people" }}
          rows={list.rows}
          rowKey={(s) => s.id}
          status={list.status}
          errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
          onRetry={list.refetch}
          hasMore={list.hasMore}
          loadingMore={list.loadingMore}
          onLoadMore={list.loadMore}
          filtered={false}
          empty={
            state === "invited"
              ? { title: "No one waiting", description: "People you invite show here until they set their password." }
              : { title: "No one here", description: "Invite staff with Invite staff." }
          }
          toolbar={
            <SegmentedControl
              label="Status"
              value={state}
              onValueChange={(v) =>
                navigate({
                  search: (s) => ({ ...s, state: v === "active" ? undefined : (v as (typeof STATES)[number] | "all") }),
                })
              }
              options={[
                { value: "active", label: "Active" },
                { value: "invited", label: "Invited" },
                { value: "deactivated", label: "Deactivated" },
                { value: "all", label: "All" },
              ]}
            />
          }
          columns={[
            {
              key: "user",
              label: "Name",
              render: (s) => (
                <span className={styles.stack}>
                  <Link to="/settings/staff/$staffId" params={{ staffId: s.id }} className={styles.link}>
                    {s.user.name}
                  </Link>
                  <span className={styles.muted}>{s.position ?? ""}</span>
                </span>
              ),
            },
            { key: "email", label: "Email", render: (s) => s.user.email ?? "—" },
            { key: "roles", label: "Roles", render: (s) => s.roles.map((r) => r.name).join(", ") || "None" },
            {
              key: "status",
              label: "Status",
              render: (s) => {
                const [label, tone] = staffBadge(s);
                return <Badge tone={tone}>{label}</Badge>;
              },
            },
            { key: "created_at", label: "Added", render: (s) => formatDate(s.created_at, format) },
          ]}
        />
      )}
      {inviting ? <InviteDialog onClose={() => setInviting(false)} /> : null}
    </>
  );
}

/** Roles as checkboxes: what each one lets them do. Shared by the invite form and the staff page. */
export function RolePicker({
  value,
  onChange,
  error,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  error?: string;
}) {
  const roles = useQuery(rolesQuery);
  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>Roles (at least one)</legend>
      {(roles.data ?? []).map((r: Role) => (
        <Checkbox
          key={r.id}
          label={r.name}
          description={r.description ?? `${r.permissions.length} permissions`}
          checked={value.includes(r.id)}
          onCheckedChange={(on) => onChange(on ? [...value, r.id] : value.filter((id) => id !== r.id))}
        />
      ))}
      {error ? (
        <span className={styles.fieldError} role="alert">
          {error}
        </span>
      ) : null}
    </fieldset>
  );
}

/**
 * Invite someone by email: they get a link to set their password (valid 72 hours), or a note to
 * sign in if they already have one. They show as Invited until they set it.
 */
function InviteDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [position, setPosition] = useState("");
  const [phone, setPhone] = useState("");
  const [roles, setRoles] = useState<string[]>([]);
  const check = useFormCheck();
  // the API's rules: InviteStaffBody in modules/staff/src/schemas.ts
  const errors = {
    name: name.trim() && name.trim().length <= 120 ? undefined : "Enter their name, up to 120 characters.",
    email: /^\S+@\S+\.\S+$/.test(email.trim()) ? undefined : "Enter their work email, like jane@example.com.",
    phone:
      !phone.trim() || /^\+\d{9,15}$/.test(phone.replace(/\s/g, ""))
        ? undefined
        : "Enter the phone as + and 9 to 15 digits, like +254712345678, or leave it empty.",
    roles: roles.length > 0 ? undefined : "Pick at least one role.",
  };
  const invite = useMutation({
    mutationFn: () =>
      teamActions.invite({
        name: name.trim(),
        email: email.trim(),
        position: position.trim() || undefined,
        phone_number: phone.replace(/\s/g, "") || undefined,
        role_ids: roles,
      }),
    onSuccess: async (staff) => {
      await queryClient.invalidateQueries({ queryKey: ["staff"] });
      onClose();
      void navigate({ to: "/settings/staff/$staffId", params: { staffId: staff.id } });
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title="Invite staff"
        description="They get an email with a link to set their password. It works for 72 hours."
      >
        <div className={styles.form}>
          <div className={styles.pair}>
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={check.show(errors.name)}
            />
            <Input
              label="Work email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={check.show(errors.email)}
            />
          </div>
          <div className={styles.pair}>
            <Input
              label="Position (optional)"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="Dispatcher, Nairobi"
            />
            <Input
              label="Phone (optional)"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+254712345678"
              description="International format"
              error={check.show(errors.phone)}
            />
          </div>
          <RolePicker value={roles} onChange={setRoles} error={check.show(errors.roles)} />
          {invite.error ? (
            <Alert tone="danger" title="Not invited">
              {messageFor(errorOf(invite.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button loading={invite.isPending} onClick={() => check.submit(errors, () => invite.mutate())}>
              Send the invitation
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
